/* =================================================================
   MERYIA — Home page behaviour
   - <meryia-carousel>   hero carousel (autoplay, swipe, arrows, dots)
   - <meryia-look>       shop-the-look pointers + add to cart
   - scroll reveal, word reveal, parallax
   Loaded (deferred) by the Meryia home sections. Safe to load twice.
   ================================================================= */
(function () {
  if (window.__meryiaHome) return;
  window.__meryiaHome = true;

  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var design = !!(window.Shopify && window.Shopify.designMode);

  /* ---------------------------------------------------------------
     Hero carousel
     --------------------------------------------------------------- */
  class MeryiaCarousel extends HTMLElement {
    connectedCallback() {
      this.slides = Array.prototype.slice.call(this.querySelectorAll('.mh-slide'));
      this.dots = Array.prototype.slice.call(this.querySelectorAll('.mh-carousel__dot'));
      this.i = Math.max(0, this.slides.findIndex(function (s) { return s.classList.contains('is-active'); }));
      if (this.slides.length < 2) return;

      this.interval = parseInt(this.dataset.interval, 10) || 6000;
      this.holds = {};
      this.effect = this.classList.contains('mh-effect-slide') ? 'slide' : 'fade';
      if (this.dataset.autoplay === 'true' && reduce) this.dataset.autoplay = 'false';
      this.autoplay = this.dataset.autoplay === 'true';

      var self = this;
      var prev = this.querySelector('[data-prev]');
      var next = this.querySelector('[data-next]');
      if (prev) prev.addEventListener('click', function () { self.step(-1); });
      if (next) next.addEventListener('click', function () { self.step(1); });
      this.dots.forEach(function (d) { d.addEventListener('click', function () { self.go(parseInt(d.dataset.dot, 10)); }); });

      this.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowLeft') self.step(-1);
        if (e.key === 'ArrowRight') self.step(1);
      });
      if (window.matchMedia && window.matchMedia('(hover: hover)').matches) {
        this.addEventListener('mouseenter', function () { self.hold('hover'); });
        this.addEventListener('mouseleave', function () { self.release('hover'); });
      }
      // only keyboard focus pauses rotation (a mouse click on an arrow must not freeze it)
      this.addEventListener('focusin', function (e) { if (e.target.matches && e.target.matches(':focus-visible')) self.hold('focus'); });
      this.addEventListener('focusout', function () { self.release('focus'); });

      // swipe (touch / pen). Vertical scrolling stays native (touch-action: pan-y).
      var vp = this.querySelector('.mh-carousel__viewport');
      var sx = null, sy = null;
      vp.addEventListener('pointerdown', function (e) { if (e.pointerType !== 'mouse') { sx = e.clientX; sy = e.clientY; } });
      vp.addEventListener('pointerup', function (e) {
        if (sx === null) return;
        var dx = e.clientX - sx, dy = e.clientY - sy;
        sx = null;
        if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.4) self.step(dx < 0 ? 1 : -1);
      });
      vp.addEventListener('pointercancel', function () { sx = null; });

      this._vis = function () { document.hidden ? self.hold('tab') : self.release('tab'); };
      document.addEventListener('visibilitychange', this._vis);

      if ('IntersectionObserver' in window) {
        this._io = new IntersectionObserver(function (en) {
          en[0].isIntersecting ? self.release('offscreen') : self.hold('offscreen');
        }, { threshold: 0.25 });
        this._io.observe(this);
      }

      // theme editor: jump to the slide being edited and stop rotating
      this._sel = function (e) {
        var s = self.querySelector('[data-block-id="' + e.detail.blockId + '"]');
        if (!s) return;
        self.hold('editor');
        self.go(self.slides.indexOf(s), true);
      };
      this._desel = function () { self.release('editor'); };
      document.addEventListener('shopify:block:select', this._sel);
      document.addEventListener('shopify:block:deselect', this._desel);
      document.addEventListener('shopify:section:deselect', this._desel);

      // the carousel is as tall as the slide being shown (slides can have different heights)
      this.vp = vp;
      this.syncHeight = function () {
        var a = self.slides[self.i];
        if (a) self.vp.style.height = a.offsetHeight + 'px';
      };
      if ('ResizeObserver' in window) {
        this._ro = new ResizeObserver(self.syncHeight);
        this.slides.forEach(function (s) { self._ro.observe(s); });
      }
      window.addEventListener('load', this.syncHeight);
      this.syncHeight();

      this.arm(this.interval);
    }

    disconnectedCallback() {
      if (this._ro) this._ro.disconnect();
      if (this.syncHeight) window.removeEventListener('load', this.syncHeight);
      clearTimeout(this.timer);
      if (this._vis) document.removeEventListener('visibilitychange', this._vis);
      if (this._io) this._io.disconnect();
      if (this._sel) {
        document.removeEventListener('shopify:block:select', this._sel);
        document.removeEventListener('shopify:block:deselect', this._desel);
        document.removeEventListener('shopify:section:deselect', this._desel);
      }
    }

    /* --- timing: one timer, paused/resumed by named "holds" --- */
    arm(ms) {
      clearTimeout(this.timer);
      this.remaining = ms;
      this.startedAt = Date.now();
      if (this.autoplay && !this.isHeld()) this.timer = setTimeout(this.step.bind(this, 1), ms);
    }
    isHeld() { return Object.keys(this.holds).length > 0; }
    hold(reason) {
      if (this.holds[reason]) return;
      var wasHeld = this.isHeld();
      this.holds[reason] = true;
      if (wasHeld) return;
      clearTimeout(this.timer);
      this.remaining = Math.max(300, this.remaining - (Date.now() - this.startedAt));
      this.classList.add('is-paused');
    }
    release(reason) {
      if (!this.holds[reason]) return;
      delete this.holds[reason];
      if (this.isHeld()) return;
      this.classList.remove('is-paused');
      this.startedAt = Date.now();
      if (this.autoplay) this.timer = setTimeout(this.step.bind(this, 1), this.remaining);
    }

    step(d) { this.go(this.i + d); }

    go(n, immediate) {
      var len = this.slides.length;
      n = ((n % len) + len) % len;
      if (n === this.i) return;
      var prev = this.slides[this.i];
      var next = this.slides[n];
      prev.classList.remove('is-active');
      if (this.effect === 'slide' && !immediate) {
        prev.classList.add('is-leaving');
        setTimeout(function () { prev.classList.remove('is-leaving'); }, 1000);
      }
      next.classList.add('is-active');
      if (this.dots.length) {
        this.dots[this.i].classList.remove('is-active');
        this.dots[this.i].removeAttribute('aria-selected');
        this.dots[n].classList.add('is-active');
        this.dots[n].setAttribute('aria-selected', 'true');
      }
      this.i = n;
      this.syncHeight();
      this.arm(this.interval);
    }
  }
  if (!customElements.get('meryia-carousel')) customElements.define('meryia-carousel', MeryiaCarousel);

  /* ---------------------------------------------------------------
     Shop the look
     --------------------------------------------------------------- */
  class MeryiaLook extends HTMLElement {
    connectedCallback() {
      var self = this;
      this.spots = Array.prototype.slice.call(this.querySelectorAll('[data-spot]'));
      this.panels = Array.prototype.slice.call(this.querySelectorAll('[data-panel]'));

      this.spots.forEach(function (s) {
        s.addEventListener('click', function () {
          var id = s.dataset.spot;
          self.current === id ? self.close() : self.open(id);
        });
      });
      this.panels.forEach(function (p) {
        p.querySelector('[data-close]').addEventListener('click', function () { self.close(true); });
        var form = p.querySelector('[data-atc]');
        form.addEventListener('submit', function (e) { e.preventDefault(); self.add(p, form); });
        var sel = form.querySelector('[data-variant]');
        if (sel) sel.addEventListener('change', function () { self.variantChanged(p, sel); });
      });
      this.addEventListener('keydown', function (e) { if (e.key === 'Escape' && self.current) self.close(true); });

      this._sel = function (e) {
        if (self.querySelector('[data-spot="' + e.detail.blockId + '"]')) self.open(e.detail.blockId);
      };
      document.addEventListener('shopify:block:select', this._sel);
    }
    disconnectedCallback() { if (this._sel) document.removeEventListener('shopify:block:select', this._sel); }

    open(id) {
      var self = this;
      this.current = id;
      this.spots.forEach(function (s) {
        var on = s.dataset.spot === id;
        s.classList.toggle('is-active', on);
        s.setAttribute('aria-expanded', on ? 'true' : 'false');
        if (on) {
          var x = parseFloat(s.style.getPropertyValue('--x')) || 50;
          self.pointerX = Math.min(94, Math.max(6, x));
        }
      });
      this.panels.forEach(function (p) {
        var on = p.dataset.panel === id;
        p.hidden = !on;
        if (on) {
          p.style.setProperty('--px', self.pointerX + '%');
          // restart the entrance animation
          p.style.animation = 'none'; void p.offsetWidth; p.style.animation = '';
        }
      });
      this.classList.add('has-open');
      var panel = this.querySelector('[data-panel="' + id + '"]');
      if (panel && !design) {
        var r = panel.getBoundingClientRect();
        if (r.bottom > window.innerHeight - 8) panel.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'nearest' });
      }
    }

    close(focusBack) {
      var id = this.current;
      this.current = null;
      this.spots.forEach(function (s) { s.classList.remove('is-active'); s.setAttribute('aria-expanded', 'false'); });
      this.panels.forEach(function (p) { p.hidden = true; });
      this.classList.remove('has-open');
      if (focusBack && id) {
        var s = this.querySelector('[data-spot="' + id + '"]');
        if (s) s.focus();
      }
    }

    variantChanged(panel, sel) {
      var o = sel.options[sel.selectedIndex];
      var price = panel.querySelector('[data-price]');
      var compare = panel.querySelector('[data-compare]');
      if (price) price.textContent = o.dataset.price;
      if (compare) {
        compare.textContent = o.dataset.compare || '';
        compare.hidden = !o.dataset.compare;
      }
    }

    async add(panel, form) {
      var btn = form.querySelector('.mh-panel__atc');
      var label = btn.querySelector('[data-atc-label]');
      var msg = form.querySelector('[data-msg]');
      var idField = form.querySelector('[name="id"]');
      var original = label.textContent;
      msg.textContent = '';
      msg.classList.remove('is-ok');
      btn.classList.add('is-loading');

      // Reuse the theme's own cart drawer / notification so the cart opens like a normal add-to-cart
      var cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      var body = { id: parseInt(idField.value, 10), quantity: 1 };
      if (cart && typeof cart.getSectionsToRender === 'function') {
        body.sections = cart.getSectionsToRender().map(function (s) { return s.id; }).join(',');
        body.sections_url = window.location.pathname;
        if (typeof cart.setActiveElement === 'function') cart.setActiveElement(document.activeElement);
      } else {
        // no drawer / notification on this theme: just refresh the header cart count
        body.sections = 'cart-icon-bubble';
        body.sections_url = window.location.pathname;
      }

      try {
        var res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify(body)
        });
        var data = await res.json();
        if (!res.ok || data.status) throw new Error(data.description || data.message || 'error');

        label.textContent = form.dataset.added || '✓';
        btn.classList.add('is-done');
        if (cart && typeof cart.renderContents === 'function' && data.sections) {
          cart.renderContents(data);
        } else if (data.sections && data.sections['cart-icon-bubble']) {
          var bubble = document.getElementById('cart-icon-bubble');
          var doc = new DOMParser().parseFromString(data.sections['cart-icon-bubble'], 'text/html').querySelector('.shopify-section');
          if (bubble && doc) bubble.innerHTML = doc.innerHTML;
        }
        var link = document.createElement('a');
        link.href = (window.routes && window.routes.cart_url) || '/cart';
        link.textContent = form.dataset.cart || 'View cart';
        msg.textContent = '';
        msg.appendChild(link);
        msg.classList.add('is-ok');
        if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
          window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'meryia-look', productVariantId: body.id, cartData: data });
        }
        setTimeout(function () { label.textContent = original; btn.classList.remove('is-done'); }, 2400);
      } catch (err) {
        msg.textContent = err && err.message ? err.message : 'Could not add to cart';
      } finally {
        btn.classList.remove('is-loading');
      }
    }
  }
  if (!customElements.get('meryia-look')) customElements.define('meryia-look', MeryiaLook);

  /* ---------------------------------------------------------------
     Scroll reveal + word reveal + parallax
     --------------------------------------------------------------- */
  var root = document.documentElement;

  function tag(el, cls, delay) {
    if (!el || el.classList.contains('mh-reveal')) return;
    // the stock theme already animates these when its own scroll animations are on
    if (el.classList.contains('scroll-trigger') || el.closest('.scroll-trigger')) return;
    el.classList.add('mh-reveal');
    if (cls) el.classList.add(cls);
    if (delay) el.style.setProperty('--mh-delay', delay + 's');
  }

  function splitWords(el) {
    if (!el || el.dataset.mhSplit) return;
    el.dataset.mhSplit = '1';
    var words = el.textContent.trim().split(/\s+/);
    el.setAttribute('aria-label', el.textContent.trim());
    el.innerHTML = words.map(function (w, i) {
      return '<span class="mh-word" aria-hidden="true"><span style="--mh-delay:' + (i * 0.07).toFixed(2) + 's">' + w.replace(/</g, '&lt;') + '</span></span>';
    }).join(' ');
    el.classList.add('mh-words');
  }

  function initReveal() {
    if (design || reduce || !('IntersectionObserver' in window)) return;
    try {
      root.classList.add('mh-js');
      var main = document.getElementById('MainContent') || document.body;

      // product cards (featured collection) — staggered
      main.querySelectorAll('.product-grid > .grid__item, .product-grid > li').forEach(function (el, i) { tag(el, '', (i % 4) * 0.1); });
      // section titles / text blocks that belong to the stock theme sections
      main.querySelectorAll('.title-wrapper-with-link, .featured-collection .title, .meryia-ugc__header, .meryia-newsletter, .meryia-story__rule').forEach(function (el) { tag(el); });
      main.querySelectorAll('.meryia-story__body, .meryia-story .m-btn').forEach(function (el, i) { tag(el, '', 0.25 + i * 0.1); });
      main.querySelectorAll('.meryia-ugc__tile').forEach(function (el, i) { tag(el, 'mh-reveal--zoom', Math.min(i, 6) * 0.08); });
      splitWords(main.querySelector('.meryia-story__title'));

      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          if (!en.isIntersecting) return;
          en.target.classList.add('is-in');
          io.unobserve(en.target);
        });
      }, { threshold: 0.12, rootMargin: '0px 0px -6% 0px' });
      main.querySelectorAll('.mh-reveal, .mh-words').forEach(function (el) { io.observe(el); });
    } catch (e) {
      root.classList.remove('mh-js');
    }
  }

  function initParallax() {
    if (reduce) return;
    var els = Array.prototype.slice.call(document.querySelectorAll('[data-parallax]'));
    if (!els.length) return;
    var visible = new Set();
    var ticking = false;

    function update() {
      ticking = false;
      var vh = window.innerHeight;
      visible.forEach(function (el) {
        var parent = el.parentElement;
        var pr = parent.getBoundingClientRect();
        var speed = parseFloat(el.dataset.speed) || 0.12;
        var slack = Math.max(0, (el.offsetHeight - parent.offsetHeight) / 2);
        var off = (pr.top + pr.height / 2 - vh / 2) * -speed;
        off = Math.max(-slack, Math.min(slack, off));
        el.style.transform = 'translate3d(0,' + off.toFixed(1) + 'px,0)';
      });
    }
    function req() { if (!ticking) { ticking = true; requestAnimationFrame(update); } }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) { en.isIntersecting ? visible.add(en.target) : visible.delete(en.target); });
      req();
    }, { rootMargin: '10% 0px' });
    els.forEach(function (el) { io.observe(el); });
    window.addEventListener('scroll', req, { passive: true });
    window.addEventListener('resize', req);
  }

  function boot() {
    if (document.querySelector('.mh-carousel, .mh-look, .mh-bundle')) root.classList.add('mh-home');
    initReveal();
    initParallax();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
